import { GoogleMap, withGoogleMap, withScriptjs } from "react-google-maps";
import * as React from "react";
import { compose, withProps } from "recompose";
import Marker from "react-google-maps/lib/components/Marker";
import googleMaps from "../api/googleMaps";
import DirectionsRenderer from "react-google-maps/lib/components/DirectionsRenderer";
import { Grid } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";

import StandaloneSearchBox from "react-google-maps/lib/components/places/StandaloneSearchBox";
import MoneyLabel from "./MoneyLabel";
import NumberFormat from "react-number-format";
import DirectionsResult = google.maps.DirectionsResult;
import PlaceResult = google.maps.places.PlaceResult;
import DirectionsLeg = google.maps.DirectionsLeg;
import DirectionsRoute = google.maps.DirectionsRoute;
import LatLngBounds = google.maps.LatLngBounds;
import { ConfigData } from "../config";
import { getApi } from "../api/ApiProvider";
import { BranchSelector, BranchData } from "./BranchSelector";
import { ClientLocationMng } from "./ClientLocationMng";

export class Dot {
  lat: any;
  lng: any;

  constructor(latitude: number, longitude: number) {
    this.lat = latitude;
    this.lng = longitude;
  }
}

export interface RouteData {
  origin?: Dot;
  originStreet?: string;
  destiny?: Dot;
  destinyStreet?: string;

  distance?: number;
  price?: number;
  duration?: string;
  fullData?: DirectionsResult;
  route?: DirectionsRoute;
  leg?: DirectionsLeg;
  searchBounds?: LatLngBounds;

  // Client document
  clientDocument?: string;
  clientLocations?: LocationData[];
  selectedClientLocationId?: string;
  selectedClientLocation?: LocationData;

  // Branch data used as origin
  selectedBranch?: BranchData;
  branchList?: BranchData[];
}

export interface RouteSelectorProps {
  data?: RouteData;
  config: ConfigData;
  onNewData: (data: RouteData) => void;
}

type MarkerType = "origin" | "destiny";

interface RouteSelectorState {
  data: RouteData;
  line?: string;
  center: Dot;
}

interface MapProps {
  zoom: number;
  center: Dot;
  origin?: Dot;
  destiny?: Dot;
  directions: any;

  handleOnClick(e: google.maps.MouseEvent | google.maps.IconMouseEvent): void;

  handleDragEnd(whichOne: string): (e: google.maps.MouseEvent) => void;
}

export interface LocationData {
  id: number;
  lat: number;
  lng: number;

  street1: string;
  client: {
    user: { name: string; email: string };
    phone: string;
  };
}

const RouteMap: React.SFC<MapProps> = (props: MapProps) => {
  const {
    center,
    zoom,
    origin,
    destiny,
    handleDragEnd,
    handleOnClick,
    directions,
  } = props;
  return (
    <GoogleMap defaultZoom={zoom} center={center} onClick={handleOnClick}>
      {origin && (
        <Marker
          draggable
          onDragEnd={handleDragEnd("origin")}
          position={origin}
          label={{ text: "O", color: "white" }}
        />
      )}
      {destiny && (
        <Marker
          draggable
          onDragEnd={handleDragEnd("destiny")}
          position={destiny}
          label={{ text: "D", color: "white" }}
        />
      )}

      {directions && (
        <DirectionsRenderer
          directions={directions}
          defaultOptions={{ suppressMarkers: true }}
        />
      )}
    </GoogleMap>
  );
};

const EnhancedMap = compose<MapProps, MapProps>(
  withProps({
    containerElement: <div style={{ height: "400px" }} />,
    mapElement: <div style={{ height: "400px" }} />,
  }),
  withGoogleMap
)(RouteMap);

class RouteSelector extends React.Component<
  RouteSelectorProps,
  RouteSelectorState
> {
  refOrigin: StandaloneSearchBox;
  refDestiny: StandaloneSearchBox;

  constructor(props: RouteSelectorProps) {
    super(props);
    this.state = {
      data: {
        originStreet: props.config.races.predefinedFrom ? "Origen" : "",
        destinyStreet: "",
        clientDocument: "",
        clientLocations: [],
        ...props.data,
      },
      center: props.config.maps.startingLocation,
    };

    this.onOriginMounted = this.onOriginMounted.bind(this);
    this.onDestinyMounted = this.onDestinyMounted.bind(this);

    this.handleOnClick = this.handleOnClick.bind(this);
    this.handleDragEnd = this.handleDragEnd.bind(this);
    this.fireNewDataEvent = this.fireNewDataEvent.bind(this);

    this.requestNewDirections = this.requestNewDirections.bind(this);
    this.getRouteDescription = this.getRouteDescription.bind(this);
    this.handleKeyPressInDocument = this.handleKeyPressInDocument.bind(this);
    this.onChangeClientLocationSelect =
      this.onChangeClientLocationSelect.bind(this);
    this.onChangeBranchSelector = this.onChangeBranchSelector.bind(this);

    this.onChangeClientLocationSelect2 =
      this.onChangeClientLocationSelect2.bind(this);
  }

  handleOnClick(args: google.maps.MouseEvent | google.maps.IconMouseEvent) {
    if (!this.state.data.origin) {
      this.setState({
        ...this.state,
        data: {
          ...this.state.data,
          origin: new Dot(args.latLng.lat(), args.latLng.lng()),
        },
      });
      this.fireNewDataEvent(true);
      return;
    }

    if (!this.state.data.destiny) {
      this.setState({
        ...this.state,
        data: {
          ...this.state.data,
          destiny: new Dot(args.latLng.lat(), args.latLng.lng()),
        },
      });
      this.fireNewDataEvent(true);
      return;
    }
  }

  async requestNewDirections(forceRename: boolean) {
    if (this.state.data.origin && this.state.data.destiny) {
      try {
        const data: DirectionsResult = await googleMaps.getDirections(
          this.state.data.origin,
          this.state.data.destiny,
          this.props.config.maps.region
        );
        this.handleDirectionsData(data, forceRename);
        console.log("se pidieron nuevas direcciones:" + data);
      } catch (err) {
        console.log(err);
        this.setState({
          data: {
            ...this.state.data,
            distance: undefined,
            duration: undefined,
            fullData: undefined,
            price: undefined,
          },
        });
      }
    }
  }

  handleDirectionsData(data: DirectionsResult, forceNewPosition = true) {
    const route = data && data.routes && data.routes[0];

    const leg = route && route.legs && route.legs[0] && route.legs[0];

    if (!leg) {
      return;
    }

    this.setState({
      data: {
        ...this.state.data,
        originStreet: forceNewPosition
          ? leg.start_address
          : this.state.data.originStreet || leg.start_address,
        destinyStreet: forceNewPosition
          ? leg.end_address
          : this.state.data.destinyStreet || leg.end_address,
        fullData: data,
        leg: leg,
        route: route,
        distance: leg.distance.value,
        duration: leg.duration.text,
        price: (leg.distance.value / 1000) * this.props.config.races.pricePerKm,
      },
    });
  }

  getRouteDescription() {
    const data = this.state.data;
    if (!data.fullData) {
      return (
        <Typography>
          Seleccione dos puntos para iniciar el calculo de ruta
        </Typography>
      );
    }

    return (
      <Typography>
        El viaje es de:{" "}
        <NumberFormat
          value={(data.distance || 0) / 1000}
          suffix={"Km."}
          displayType={"text"}
          decimalScale={1}
        />{" "}
        , y costará aproximadamente <MoneyLabel amount={data.price} />.
      </Typography>
    );
  }

  handleDragEnd(marker: MarkerType) {
    return (e: google.maps.MouseEvent) => {
      this.setState({
        data: {
          ...this.state.data,
          [marker]: new Dot(e.latLng.lat(), e.latLng.lng()),
        },
      });
      this.fireNewDataEvent(true);
    };
  }

  fireNewDataEvent(forceRename: boolean) {
    if (this.state.data.origin && this.state.data.destiny) {
      this.requestNewDirections(forceRename).then(() => {
        this.props.onNewData(this.state.data);
      });
    }
  }

  setNewPlace(ref: StandaloneSearchBox, type: MarkerType) {
    const places: PlaceResult[] = ref.getPlaces();
    const bounds = new google.maps.LatLngBounds();

    places.forEach((place: PlaceResult) => {
      if (place && place.geometry && place.geometry.viewport) {
        bounds.union(place.geometry.viewport);
      } else {
        if (place && place.geometry && place.geometry.location) {
          bounds.extend(place.geometry.location);
        }
      }
    });

    const nextMarkers = places.map((place: PlaceResult) => {
      if (place && place.geometry && place.geometry.location) {
        return {
          position: place.geometry.location,
        };
      } else {
        return { position: this.state.center };
      }
    });
    const nextCenter =
      (nextMarkers && nextMarkers[0] && nextMarkers[0].position) ||
      this.state.center;
    const centerDot = new Dot(nextCenter.lat(), nextCenter.lng());

    this.setState({
      data: {
        ...this.state.data,
        [type]: centerDot,
      },
      center: centerDot,
    });
    this.fireNewDataEvent(true);
  }

  onPlaceSelected(type: MarkerType) {
    return () => {
      this.setNewPlace(
        type === "origin" ? this.refOrigin : this.refDestiny,
        type
      );
    };
  }

  onOriginMounted(ref: StandaloneSearchBox) {
    this.refOrigin = ref;
    this.getSearchRegionBounds();
    this.getBranchList();
  }

  onDestinyMounted(ref: StandaloneSearchBox) {
    this.refDestiny = ref;
  }

  async getSearchRegionBounds() {
    const bounds: LatLngBounds = await googleMaps.getGeoCodeBounds(
      this.props.config.maps.region
    );
    this.setState({
      data: {
        ...this.state.data,
        searchBounds: bounds,
      },
    });
  }

  getLocationsByClientDocument(document: string) {
    getApi()
      .admin()
      .clientLocationByDocument(document)
      .then((data) => {
        this.setState({
          data: {
            ...this.state.data,
            clientLocations: data,
          },
        });
      })
      .catch((error) => {
        this.setState({
          data: {
            ...this.state.data,
            clientLocations: undefined,
            selectedClientLocation: undefined,
            selectedClientLocationId: undefined,
          },
        });
      });
  }

  handleKeyPressInDocument(e: React.KeyboardEvent<any>) {
    if (e.key === "Enter") {
      if (this.state.data.clientDocument) {
        this.getLocationsByClientDocument(this.state.data.clientDocument);
      }
    }
  }

  onChangeClientLocationSelect(e: any) {
    const location = this.getClientLocationByName(e.target.value);
    if (location) {
      this.setState(
        {
          data: {
            ...this.state.data,
            selectedClientLocationId: e.target.value,
            selectedClientLocation: location,
            destinyStreet: location.street1,
            destiny: new Dot(location.lat, location.lng),
          },
          center: new Dot(location.lat, location.lng),
        },
        () => this.fireNewDataEvent(true)
      );
    }

    console.log(
      `selectedClientLocation--> ${this.state.data.selectedClientLocation}`
    );
  }

  getClientLocationByName(id: string): LocationData | undefined {
    const nid = Number(id);
    if (this.state.data.clientLocations) {
      const loc = this.state.data.clientLocations.find((l) => l.id === nid);

      return loc ? loc : undefined;
    }
    return undefined;
  }

  getBranchList() {
    getApi()
      .branch()
      .listAllBranchesAdmin()
      .then((data) => {
        this.setState(
          {
            data: {
              ...this.state.data,
              branchList: data,
            },
          },
          () => {
            // default first branch selection
            this.setBranch(0);
          }
        );
      });
  }

  onChangeBranchSelector(e: React.ChangeEvent<any>) {
    const idx = +e.target.value;
    this.setBranch(idx);
  }

  setBranch(idx: number) {
    const branch = this.state.data.branchList
      ? this.state.data.branchList[idx]
      : undefined;
    const origin = this.state.data.origin;
    console.log(idx);
    this.setState({
      data: {
        ...this.state.data,
        selectedBranch: branch,
        origin: branch
          ? new Dot(branch.location.lat, branch.location.lng)
          : origin,
      },
    });
  }

  onChangeClientLocationSelect2(index: number) {
    console.log(index);
    if (index < 0) {
      this.setState({
        data: {
          ...this.state.data,
          selectedClientLocation: undefined,
          selectedClientLocationId: undefined,
        },
      });
    }

    const location =
      this.state.data.clientLocations && this.state.data.clientLocations[index];
    if (location && index >= 0) {
      this.setState(
        {
          data: {
            ...this.state.data,
            selectedClientLocationId: index.toString(),
            selectedClientLocation: location,
            destinyStreet: location.street1,
            destiny: new Dot(location.lat, location.lng),
          },
          center: new Dot(location.lat, location.lng),
        },
        () => this.fireNewDataEvent(true)
      );
    }
  }

  render() {
    const defaultLocation = {
      id: 0,
      lat: 0,
      lng: 0,
      street1: "",
      client: {
        user: { name: "", email: "" },
        phone: "",
      },
    };
    let clientLocations: LocationData[] = [defaultLocation];

    if (
      this.state.data.clientLocations &&
      this.state.data.clientLocations.length > 0
    ) {
      clientLocations = this.state.data.clientLocations;
      const newLocation = {
        id: 0,
        lat: 0,
        lng: 0,
        street1: "",
        client: {
          user: {
            name: clientLocations[0].client.user.name,
            email: clientLocations[0].client.user.email,
          },
          phone: clientLocations[0].client.phone,
        },
      };
      clientLocations.push(newLocation);
    }
    return (
      <Grid container>
        <Grid item md={4}>
          <Typography variant="caption">
            Busca en las casillas de abajo y se actualizará el mapa
          </Typography>

          <h4>Origen</h4>

          <BranchSelector
            branchList={this.state.data.branchList}
            onChange={this.onChangeBranchSelector}
          />

          <form noValidate autoComplete="off">
            <StandaloneSearchBox
              ref={this.onOriginMounted}
              bounds={this.state.data.searchBounds}
              onPlacesChanged={this.onPlaceSelected("origin")}
            >
              <TextField
                id="origin"
                label="Otra dirección de salida"
                value={this.state.data.originStreet}
                style={{
                  width: "90%",
                }}
                margin="normal"
                onChange={(e) =>
                  this.setState({
                    data: {
                      ...this.state.data,
                      originStreet: e.target.value,
                    },
                  })
                }
                placeholder="Desde donde?"
              />
            </StandaloneSearchBox>

            <br />
            <h4>Destino</h4>
            <TextField
              id="clientDocumentSearch"
              label="Documento del cliente"
              value={this.state.data.clientDocument}
              style={{ width: "90%" }}
              margin="normal"
              placeholder="Documento del cliente"
              onChange={(e) =>
                this.setState({
                  data: {
                    ...this.state.data,
                    clientDocument: e.target.value,
                  },
                })
              }
              onKeyPress={this.handleKeyPressInDocument}
            />
            <div>
              {(this.state.data.selectedClientLocation &&
                this.state.data.selectedClientLocation.client.user.name) ||
                ""}
            </div>
            <Select
              value={this.state.data.selectedClientLocationId}
              inputProps={{
                name: "location",
                id: "clientLocationClient",
              }}
              onChange={this.onChangeClientLocationSelect}
            >
              {clientLocations.map((location, index) => (
                <MenuItem key={index} value={location.id}>
                  {location.street1}
                </MenuItem>
              ))}
            </Select>
            <ClientLocationMng
              locationList={this.state.data.clientLocations}
              clientDocument={this.state.data.clientDocument}
              onChange={this.onChangeClientLocationSelect2}
            />

            <br />

            <br />
            <StandaloneSearchBox
              ref={this.onDestinyMounted}
              bounds={this.state.data.searchBounds}
              onPlacesChanged={this.onPlaceSelected("destiny")}
            >
              <TextField
                id="destiny"
                label="Dirección del cliente"
                value={this.state.data.destinyStreet}
                onChange={(e) =>
                  this.setState({
                    data: {
                      ...this.state.data,
                      destinyStreet: e.target.value,
                    },
                  })
                }
                style={{
                  width: "90%",
                }}
                margin="normal"
                placeholder="Hasta donde?"
              />
            </StandaloneSearchBox>
          </form>
          <br />
        </Grid>
        <Grid item md={8}>
          <EnhancedMap
            center={this.state.center}
            zoom={this.props.config.maps.startingLocation.zoom}
            destiny={this.state.data.destiny}
            origin={this.state.data.origin}
            directions={this.state.data.fullData}
            handleDragEnd={this.handleDragEnd}
            handleOnClick={this.handleOnClick}
          ></EnhancedMap>
        </Grid>

        <Grid item md={12}>
          {this.getRouteDescription()}
        </Grid>
      </Grid>
    );
  }
}

export default compose<any, RouteSelectorProps>(
  withProps((props: RouteSelectorProps) => {
    return {
      googleMapURL: `https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry,drawing,places&key=${props.config.maps.key}`,
      loadingElement: <div style={{ height: "400px" }} />,
    };
  }),
  withScriptjs
)(RouteSelector);



// WEBPACK FOOTER //
// ./src/components/routeSelector.tsx